const API_BASE =
  import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ||
  'https://api.readsy.link';

const JSON_HEADERS = { 'content-type': 'application/json' };

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...JSON_HEADERS,
      ...(options.headers || {}),
    },
    credentials: 'include',
  });

  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    const message = payload?.error || res.statusText || 'Request failed';
    throw new Error(message);
  }

  return payload as T;
}

export async function requestMagicLink(email: string, turnstileToken: string) {
  return request<{ ok: boolean }>('/auth/magic-link', {
    method: 'POST',
    body: JSON.stringify({ email, turnstileToken }),
  });
}

export async function verifyMagicLink(token: string): Promise<string> {
  const data = await request<{ ok: boolean; email: string }>('/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  return data.email;
}

export async function listBooks(params?: { q?: string; genre?: string }) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.genre) qs.set('genre', params.genre);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<{ books: any[] }>(`/books${suffix}`);
}

export async function getBook(id: string) {
  return request(`/books/${id}`);
}

export async function getChapter(bookId: string, chapterId: string) {
  return request(`/books/${bookId}/chapters/${chapterId}`);
}

export async function getComments(bookId: string, chapterId: string) {
  return request<{ comments: any[] }>(`/books/${bookId}/chapters/${chapterId}/comments`);
}

export async function postComment(bookId: string, chapterId: string, content: string) {
  return request(`/books/${bookId}/chapters/${chapterId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

export async function getLibrary() {
  return request(`/me/library`);
}

export async function saveProgress(payload: { bookId: string; chapterId: string; percentage: number; minutes?: number }) {
  return request(`/me/progress`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getBookmarks() {
  return request<{ bookmarks: any[] }>(`/me/bookmarks`);
}

export async function addBookmark(payload: { bookId: string; chapterId: string; excerpt?: string }) {
  return request(`/me/bookmarks`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function deleteBookmark(id: string) {
  return request(`/me/bookmarks/${id}`, {
    method: 'DELETE',
  });
}

export async function createBook(payload: {
  title: string;
  author: string;
  genre: string;
  description: string;
  language: string;
  chapters: { title: string; content: string; isFree?: boolean }[];
}) {
  return request<{ ok: boolean; id: string }>(`/books`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getStats() {
  return request(`/me/stats`);
}

export async function getMe() {
  return request<{ authenticated: boolean; email?: string; userId?: string }>(`/auth/me`, { method: 'GET' });
}

export async function listMyBooks() {
  return request<{ books: any[] }>(`/me/books`);
}

export async function getMyBook(id: string) {
  return request(`/me/books/${id}`);
}

export async function updateBook(id: string, payload: {
  title: string;
  author: string;
  genre: string;
  description: string;
  language: string;
  chapters: { title: string; content: string; isFree?: boolean }[];
}) {
  return request(`/books/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function removeBook(id: string) {
  return request(`/books/${id}`, { method: 'DELETE' });
}

export { API_BASE };
